import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import '../ExternalUsers/CreateExternalModal.css';

const BlockDateAndTimeModal = ({ isOpen, onClose, blockedDatesAndTime, setBlockedDatesAndTime }) => {
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [error, setError] = useState(null);
    const [timeOptions, setTimeOptions] = useState([]);
    const [isTimeDisabled, setIsTimeDisabled] = useState(true);
    const [dentist, setDentist] = useState(''); // State for selected dentist
    const [dentistList, setDentistList] = useState([]); // State for list of dentists

    // Fetch dentists from the API
    useEffect(() => {
        const fetchDentists = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/dentists');
                if (!response.ok) {
                    throw new Error('Failed to fetch dentists');
                }
                const dentists = await response.json();
                setDentistList(dentists); // Store the fetched dentists
            } catch (error) {
                console.error('Error fetching dentists:', error);
            }
        };

        fetchDentists();
    }, []);

    // Generate time options in 30-minute intervals
    // Generate time options in 30-minute intervals from 08:00 to 18:00
    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 8; hour <= 17; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const timeString = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                options.push(timeString);
            }
        }
        // Include 18:00 explicitly as the last option
        options.push('18:00');
        return options;
    };

    // Filter out blocked times for the selected date
    const filterBlockedTimes = (selectedDate) => {
        const blockedTimesOnDate = blockedDatesAndTime
            .filter((blocked) => blocked.startsWith(selectedDate))
            .map((blocked) => blocked.split('T')[1].substring(0, 5)); // Extract time (HH:MM)

        return generateTimeOptions().filter((time) => !blockedTimesOnDate.includes(time));
    };

    // Update time options when date changes
    useEffect(() => {
        if (date) {
            const availableTimes = filterBlockedTimes(date);
            setTimeOptions(availableTimes);
            setIsTimeDisabled(false);
            setTime(''); // Reset time when date changes
        } else {
            setIsTimeDisabled(true);
            setTime('');
        }
    }, [date, blockedDatesAndTime]);

    const handleBlockDate = async () => {
        if (!date || !time) {
            setError('Please select a valid date and time.');
            return;
        }

        // Combine date and time into a single datetime string
        const dateTime = `${date}T${time}:00.000Z`;

        // Check if the selected datetime is before now
        const selectedDateTime = new Date(dateTime);
        const now = new Date();

        if (selectedDateTime < now) {
            setError('You cannot select a date and time in the past.');
            return;
        }

        // Check if the datetime is already blocked
        if (blockedDatesAndTime.includes(dateTime)) {
            setError('This date and time is already blocked.');
            return;
        }

        try {
            const response = await fetch('https://api.hgcdental.clinic/api/blockedDatesAndTime', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ date: dateTime, dentist }),
            });

            if (response.ok) {
                window.alert('Date and Time blocked successfully!');
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to block date and time');
            }

            const newBlockedDate = await response.json();
            const updatedBlockedDatesAndTime = [...blockedDatesAndTime, newBlockedDate.date];
            setBlockedDatesAndTime(updatedBlockedDatesAndTime);
            setDate('');
            setTime('');
            setError(null);
            onClose(); // Close the modal after blocking the date and time
        } catch (error) {
            console.error('Error blocking date and time:', error.message);
            setError(error.message || 'Failed to block date and time. Please try again.');
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            className="modal"
            overlayClassName="overlay"
        >
            <div className="modal-flex-div">
                <button className="close-icon" onClick={onClose}>&times;</button>
                <h2 className="h2-modal">Block Date And Time</h2>
            </div>
            <div className="modal-divider" />

            {error && <div className="error-message">{error}</div>}

            <div>
                <label>Dentist*</label>
                <select
                    value={dentist}
                    onChange={(e) => setDentist(e.target.value)}
                    className="modal-input"
                    required
                >
                    <option value="">Select Dentist</option>
                    {dentistList.map((dentist) => (
                        <option key={dentist._id} value={dentist.lastName}>
                            {dentist.firstName} {dentist.lastName}
                        </option>
                    ))}
                </select>
            </div>

            <div>
                <label>Block Date*</label>
                <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="modal-input"
                    min={new Date().toISOString().split('T')[0]} // Prevent selecting dates before today
                    required
                />
            </div>

            <div>
                <label>Block Time*</label>
                <select
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="modal-input"
                    disabled={isTimeDisabled}
                    required
                >
                    <option value="" disabled>Select time</option>
                    {timeOptions.map((timeOption) => (
                        <option key={timeOption} value={timeOption}>
                            {timeOption}
                        </option>
                    ))}
                </select>
            </div>

            <div className="padding-top" />
            <div className="modal-divider-bottom" />
            <div className="padding-top" />

            <button
                type="submit"
                onClick={handleBlockDate}
            >
                Block Date and Time
            </button>
        </Modal>
    );
};

export default BlockDateAndTimeModal;